
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

const Login: React.FC = () => {
  const { signIn, isAuthLoading } = useAuth();
  const { direction } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isLogout = searchParams.get('logout') === 'true';

  useEffect(() => {
    // If we're not in logout mode, check if user is already logged in
    if (!isLogout) {
      const checkAuth = async () => {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          // User is already logged in, redirect to dashboard
          navigate('/dashboard');
        }
      };
      
      checkAuth();
    } else {
      // If we are in logout mode, perform a signOut operation
      const performLogout = async () => {
        await supabase.auth.signOut();
        // Remove the logout parameter after signout is complete
        navigate('/login', { replace: true });
      };
      
      performLogout();
    }
  }, [navigate, isLogout]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Clear any previous session data
      await supabase.auth.signOut();
      
      await signIn(email, password);
      toast({
        title: 'Login successful!',
        description: 'You have successfully logged in.',
      });
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Login failed.',
        description: error.message || 'Invalid credentials.',
      });
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>Enter your email and password to login</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button disabled={isAuthLoading} type="submit" className="w-full">
              {isAuthLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="text-sm text-muted-foreground">
          Don't have an account? <Link to="/register" className="text-primary">Register</Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
